<template>
  <v-card>
    <v-toolbar v-if="!modal" flat color="white" density="compact">
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="selected_model"
          :return-object="false"
          item-value="value"
          item-title="text"
          :items="$lodash.sortBy(models, 'text')"
          hide-details
          single-line>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('model_type') }}</span>
        </template>
      </v-autocomplete>
      <!--RIGHT PART START (BUTTONS)-->
      <v-spacer/>
      <div v-if="$store.getters.getUser.superuser">
        <v-switch style="float: left;" color="success" density="compact" hide-details v-model="apply_new_users" @click.stop=""/>
        <span class="pl-3 sm-link" style="display: block; height: 40px; align-content: center;">{{ $store.getters.translate('apply_for_new_users') }}</span>
      </div>
      <div v-if="$store.getters.getUser.superuser" class="ml-10">
        <v-switch style="float: left;" color="success" density="compact" hide-details v-model="apply_all_users" @click.stop=""/>
        <span class="pl-3 sm-link" style="display: block; height: 40px; align-content: center;">{{ $store.getters.translate('apply_for_all_users') }}</span>
      </div>
      <v-spacer/>
      <div class="chip-container">
        <!--CREATE CUSTOM FIELD ON FIELDS PAGE-->
        <base-action-button v-if="canCreate && (selected_model === 'Company' || selected_model === 'Contact' || selected_model === 'Document' ||
                   selected_model === 'Employee' || selected_model === 'Project' || selected_model === 'Inspection' || selected_model === 'Defect' ||
                   selected_model === 'Article' || selected_model === 'TagNumber')" @action="createField">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
        <!--SAVE-->
        <base-action-button v-if="canEdit" @action="save()">
          <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("save") }}
        </base-action-button>
      </div>
    </v-toolbar>
    <v-data-table
        id="base_table_fields"
        :headers="fields"
        :items="getRecords"
        class="elevation-1"
        :show-select="false"
        :items-per-page="100">
      <template #body="{ items }">
        <draggable :disabled="!canEdit" :list="items" tag="tbody" @end="endDrag(items)" item-key="id">
          <template #item="{ element:field }">
            <tr class="sm-normal text-primary">
              <td><PhArrowsVertical size="20" weight="bold" style="cursor: pointer; vertical-align: middle;"/></td>
              <td>{{ field.sort_order }}</td>
              <td><a class="sm-bold" @click="editField(field)">{{ $store.getters.translate(field.name) }}</a></td>
              <td>{{ $store.getters.translate(field.field_type) }}</td>
              <td v-if="$store.getters.getUser.superuser"><v-checkbox density="compact" color="success" hide-details v-model="field.mandatory"/></td>
              <td><v-checkbox :readonly="!canEdit" density="compact" color="success" hide-details v-model="field.show_in_search"/></td>
              <td v-if="$store.getters.getUser.superuser"><v-checkbox density="compact" color="success" hide-details v-model="field.show_on_creation"/></td>
              <td v-if="$store.getters.getUser.superuser"><v-checkbox density="compact" color="success" hide-details v-model="field.show_on_update"/></td>
              <!--ACTION BUTTONS-->
              <td style="text-align: center; width: 80px;" v-if="!modal && (canEdit || canDelete)">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn elevation="0" icon v-bind="props">
                      <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-if="canEdit" @click="editField(field)">
                      <template #prepend><PhPencilSimpleLine size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canDelete && field.custom_field == 1" @click="deleteRecord(field.id)">
                      <template #prepend><PhTrash size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
      <template #bottom/>
    </v-data-table>
  </v-card>
  <create-field ref="create_field" @refresh="load"/>
  <edit-field ref="edit_field" @refresh="load"/>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions.js";
import draggable from "vuedraggable";
import BaseActionButton from "./BaseActionButton";
import CreateField from "../../views/fields/Create";
import EditField from "../../views/fields/Edit";
import { PhArrowsVertical, PhDotsThreeVertical, PhFloppyDisk, PhPencilSimpleLine, PhPlus, PhTrash } from "@phosphor-icons/vue";

export default {
  props: ["current_model", "modal"],
  components: {
    EditField,
    CreateField,
    draggable,
    BaseActionButton,
    PhArrowsVertical, PhTrash, PhPencilSimpleLine, PhDotsThreeVertical, PhFloppyDisk, PhPlus
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.fields,
      all_records: [],
      records: [],
      fields: [],
      models: [],
      selected_model: "",
      apply_new_users: false,
      apply_all_users: false,
    };
  },
  beforeMount() {
    document.querySelector('#base_table_fields > .v-table__wrapper > table > tbody')?.remove();
  },
  mounted() {
    const sortableTbody = document.querySelector('#base_table_fields > .v-table__wrapper > table > tbody > tbody');
    // <draggable> ruin view in <tbody> (create a <div> tag inside <tbody>)
    //FIX: https://github.com/SortableJS/Vue.Draggable/issues/1212#issuecomment-1918880591
    if (sortableTbody) {
      const tbody = document.querySelector('#base_table_fields > .v-table__wrapper > table > tbody')
      tbody.parentNode.append(sortableTbody);
      tbody.remove()
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      let response = await helpFunctions.getModelsForTables(this.$toast, this.page.name);
      this.all_records = response.data;
      let add_actions_column = !this.modal && (this.canEdit || this.canDelete);
      this.fields = helpFunctions.composeFields(helpFunctions.replaceIDwithNameBaseTable(response.fields), add_actions_column);
      if(this.selected_model === '') {
        this.fillModelsDropdown();
      }
      this.showRecords();
    },
    fillModelsDropdown() {
      if(this.modal) {
        this.selected_model = this.current_model;
      }
      else {
        //NO NEED TO SHOW USERS THESE MODELS BECAUSE IN ANY WAY WE DON`T SHOW THEM IN TABLES SO THEIR SETTINGS WILL BE NEVER USED
        let already_processed_models = ['Action','ProcedureResult','ProjectManagementAnswer','ReassignedItem','Trigger'];
        this.all_records.forEach((field) => {
          if(!already_processed_models.includes(field.model_type)) {
            already_processed_models.push(field.model_type);
            if(this.$store.getters.getUser.superuser || this.can("show", this.$createEntity(field.model_type.toLowerCase(), { id: 0 }))) {
              this.models.push({value: field.model_type, text: this.$store.getters.translate(field.model_type.toLowerCase())});
            }
          }
        });
        if (this.models.length > 0) {
          this.selected_model = this.models[0].value;
        }
      }
    },
    showRecords() {
      this.records = [];
      for (let i = 0; i < this.all_records.length; i++) {
        if (this.all_records[i].model_type.toLowerCase() === this.selected_model.toLowerCase()) {
          this.records.push(this.all_records[i]);
        }
      }
    },
    save() {
      for (let i = 0; i < this.records.length; i++) {
        this.records[i].apply_new_users = this.apply_new_users;
        this.records[i].apply_all_users = this.apply_all_users;
        helpFunctions.saveModelSilently(this.page.name, this.records[i]);
      }
      this.$toast.success(this.$store.getters.translate("successfully_saved"));
    },
    endDrag(items) {
      let temp_records = this.records;
      for (let i = 0; i < temp_records.length; i++) {
        for (let z = 0; z < items.length; z++) {
          if (temp_records[i].id === items[z].id && temp_records[i].sort_order !== z + 1) {
            this.records[i].sort_order = z + 1;
          }
        }
      }
      this.records.sort((a, b) => a.sort_order - b.sort_order); // The table was not re-sorted in the previous algorithm, although the values were changed correctly.
    },
    createField() {
      this.$refs.create_field.load(this.selected_model);
    },
    editField(item) {
      this.$refs.edit_field.load(item.id);
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          await this.load();
        }
      });
    },
  },
  computed: {
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canCreate() {
      return this.can("create", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
  watch: {
    selected_model: {
      handler() {
        this.showRecords();
      },
    },
  },
};
</script>

<style scoped>
.v-checkbox {
  display: flex;
}
.v-text-field >>> input {
  font-size: 12px !important;
}
.v-text-field >>> label {
  font-size: 12px !important;
}
.chip-container {
  display: flex;
  gap: 8px;
  margin-right: 16px;
}
</style>